import { useRef } from "react";
import { motion, useInView } from "framer-motion";

const STATS = [
  { value: "30+", label: "International Awards" },
  { value: "15+", label: "Years Experience" },
  { value: "1000+", label: "Permanent Works" },
  { value: "5+", label: "Countries Featured" },
];

export default function AboutSection() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section
      data-testid="about-section"
      ref={ref}
      className="py-24 md:py-32 bg-ink-900"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
          {/* Image */}
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8 }}
            className="relative"
          >
            <div className="relative overflow-hidden rounded-sm">
              <img
                src="https://customer-assets.emergentagent.com/job_lud-hub/artifacts/rmyfb3je_DSC01683.jpeg"
                alt="Lud Bradley — Black & Grey Realism Tattoo Artist, New Westminster BC"
                className="w-full h-[350px] md:h-[650px] object-cover object-top hover:scale-105 transition-transform duration-700"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-ink-900/60 to-transparent" />
            </div>
            {/* Accent border */}
            <div className="absolute -bottom-4 -right-4 w-full h-full border border-verde/20 rounded-sm -z-10" />
          </motion.div>

          {/* Content */}
          <motion.div
            initial={{ opacity: 0, x: 40 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <p className="font-manrope text-verde text-xs tracking-[0.3em] uppercase mb-4">About the Artist</p>
            <div className="section-divider mb-8" />

            <h2 className="font-cinzel text-4xl md:text-5xl font-bold text-[#E5E5E5] mb-8 leading-tight">
              The Master<br />of Shades
            </h2>

            <div className="space-y-5 text-[#A3A3A3] font-manrope text-sm leading-relaxed">
              <p>
                Originally from Venezuela, Lud Bradley is a New Westminster-based tattoo artist internationally recognized for his extraordinary mastery of Black and Grey Realism. With over a decade of experience and more than 30 awards worldwide, Lud pushes the boundaries of what&apos;s possible with needle and ink.
              </p>
              <p>
                His pieces are not tattoos — they are permanent masterworks, each one a story told in shadow and light. Every commission is a unique collaboration, built on consultation, trust, and an uncompromising dedication to excellence.
              </p>
              <p>
                Based at <a href="https://avalontattoocollective.ca" target="_blank" rel="noopener noreferrer" className="text-verde hover:text-verde/70 transition-colors underline underline-offset-2">Avalon Tattoo Collective</a> in New Westminster, BC, Lud also travels internationally for guest spots, conventions, and exclusive seminars where he shares his technique with the next generation of realism artists.
              </p>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 gap-6 mt-12 pt-8 border-t border-ink-600">
              {STATS.map((stat, i) => (
                <motion.div
                  key={stat.label}
                  initial={{ opacity: 0, y: 20 }}
                  animate={inView ? { opacity: 1, y: 0 } : {}}
                  transition={{ delay: 0.5 + i * 0.1 }}
                  data-testid={`stat-${i}`}
                >
                  <p className="font-cinzel text-3xl font-bold text-verde">{stat.value}</p>
                  <p className="font-manrope text-xs text-[#525252] tracking-widest uppercase mt-1">{stat.label}</p>
                </motion.div>
              ))}
            </div>

            <div className="mt-10">
              <button
                data-testid="about-cta"
                className="btn-ghost"
                onClick={() => document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" })}
              >
                Book a Consultation
              </button>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
